/**
 * DSE workstation check, instrument v2.
 *
 * Twenty-four scored questions across the six HSE risk-factor categories, plus
 * a work-location question asked first. The bank, the category maxima, the
 * RAG rule and the review-required rule are ported from the shipped
 * vibe_app/src/lib/dseAssessment.ts, and the test file checks this scorer
 * against that code verbatim.
 *
 * Not ported: the per-question advice copy. It makes causal claims from setup
 * to symptoms ("a low screen is a leading cause of neck strain"), which
 * decision C3 bars. Guidance for a flagged category is compliance-framed
 * content, sourced through the claims table, and lives in packages/content.
 *
 * Every option has a stable value; the score is looked up from it. A missing
 * answer scores 0 as it did in v2, and the result says whether the set was
 * complete so a caller can refuse to store a partial one.
 */

export const DSE_INSTRUMENT_VERSION = "v2";

export type WorkLocation = "office" | "home_desk" | "home_improvised" | "home_sofa" | "hybrid";

export const WORK_LOCATIONS: readonly { value: WorkLocation; label: string }[] = [
  { value: "office", label: "In an office or workplace provided by my employer" },
  { value: "home_desk", label: "At home, at a fixed desk or dedicated space" },
  { value: "home_improvised", label: "At home, at a kitchen or dining table" },
  { value: "home_sofa", label: "On a sofa, armchair, or in bed" },
  { value: "hybrid", label: "A mix, I move between locations" },
];

export type DseCategory = 1 | 2 | 3 | 4 | 5 | 6;
export type DseScore = 0 | 1 | 2;
export type Rag = "green" | "amber" | "red";
export type DseHeadline = "low_risk" | "some_adjustments" | "high_load";

export interface DseOption {
  value: string;
  label: string;
  score: DseScore;
}

export interface DseItem {
  id: string;
  category: DseCategory;
  prompt: string;
  options: readonly DseOption[];
  /** Hidden and scored 0 when the named answer was given to the named item. */
  skipWhen?: { item: string; value: string };
}

export const DSE_CATEGORY_LABEL: Readonly<Record<DseCategory, string>> = {
  1: "Display screen",
  2: "Keyboard",
  3: "Mouse and input",
  4: "Furniture",
  5: "Work environment",
  6: "Software and task design",
};

/** Maximum penalty per category. Totals 45. */
export const DSE_CATEGORY_MAX: Readonly<Record<DseCategory, number>> = {
  1: 12,
  2: 6,
  3: 4,
  4: 13,
  5: 6,
  6: 4,
};

const o = (value: string, label: string, score: DseScore): DseOption => ({ value, label, score });

export const DSE_ITEMS: readonly DseItem[] = [
  {
    id: "q1_1",
    category: 1,
    prompt: "Where is the top of your screen relative to your eyes when sitting upright?",
    options: [
      o("0_0", "At or just below eye level", 0),
      o("1_1", "Noticeably above eye level", 1),
      o("2_2", "Noticeably below eye level (looking down)", 2),
    ],
  },
  {
    id: "q1_2",
    category: 1,
    prompt: "About how far is your screen from your eyes?",
    options: [
      o("0_0", "Roughly an arm's length away (50 to 70cm)", 0),
      o("1_1", "Much closer or much further than that", 1),
    ],
  },
  {
    id: "q1_3",
    category: 1,
    prompt: "Do you get glare or reflections on your screen from windows or lights?",
    options: [
      o("0_0", "No, the screen is clear", 0),
      o("1_1", "Sometimes, at certain times of day", 1),
      o("2_2", "Yes, regularly", 2),
    ],
  },
  {
    id: "q1_4",
    category: 1,
    prompt: "How do you use your laptop screen?",
    options: [
      o("0_0", "I use a separate monitor as my main screen", 0),
      o("0_1", "I use a laptop stand with an external keyboard and mouse", 0),
      o("1_2", "I use the laptop screen directly, sometimes propped up", 1),
      o("2_3", "I use the laptop screen flat on the desk as my main display", 2),
    ],
  },
  {
    id: "q1_5",
    category: 1,
    prompt: "Do you use more than one screen?",
    options: [
      o("0_0", "No, just one screen", 0),
      o("1_1", "Yes, main screen ahead, second angled to the side", 1),
      o("0_2", "Yes, both centred or side by side in front of me", 0),
      o("2_3", "Yes, I turn my head significantly for my second screen", 2),
    ],
  },
  {
    id: "q1_6",
    category: 1,
    prompt: "Are your two screens at roughly the same height?",
    options: [
      o("0_0", "Yes, roughly the same height", 0),
      o("1_1", "No, one is noticeably higher or lower", 1),
    ],
    skipWhen: { item: "q1_5", value: "0_0" },
  },
  {
    id: "q1_7",
    category: 1,
    prompt: "Is the text on your screen clear and steady, easy to read without straining?",
    options: [
      o("0_0", "Yes, clear and comfortable to read", 0),
      o("1_1", "A bit small or I sometimes strain to read it", 1),
      o("2_2", "No, text is hard to read, flickers, or is often blurry", 2),
    ],
  },
  {
    id: "q2_1",
    category: 2,
    prompt: "When typing, how are your wrists positioned?",
    options: [
      o("0_0", "Straight, level with my forearms", 0),
      o("1_1", "Bent up or down", 1),
      o("2_2", "Bent to the side", 2),
      o("1_3", "I'm not sure", 1),
    ],
  },
  {
    id: "q2_2",
    category: 2,
    prompt: "When typing, are your forearms supported (resting on the desk or armrests)?",
    options: [
      o("0_0", "Yes, supported, roughly parallel to the floor", 0),
      o("1_1", "Partly supported", 1),
      o("2_2", "No, they're unsupported", 2),
    ],
  },
  {
    id: "q2_3",
    category: 2,
    prompt: "What keyboard do you mainly use?",
    options: [
      o("0_0", "A separate, full-size external keyboard", 0),
      o("1_1", "The laptop keyboard, with the laptop raised", 1),
      o("2_2", "The laptop keyboard, with the laptop flat on the desk", 2),
    ],
  },
  {
    id: "q3_1",
    category: 3,
    prompt: "Where is your mouse relative to your keyboard?",
    options: [
      o("0_0", "Right beside it, easy to reach without stretching", 0),
      o("1_1", "I reach slightly to one side", 1),
      o("2_2", "Far to the side, or over a number pad, I stretch for it", 2),
    ],
  },
  {
    id: "q3_2",
    category: 3,
    prompt: "Does using your mouse cause any aching in your hand, wrist, or forearm?",
    options: [o("0_0", "No", 0), o("1_1", "Occasionally", 1), o("2_2", "Yes, regularly", 2)],
  },
  {
    id: "q4_1",
    category: 4,
    prompt: "With your feet flat on the floor or a footrest, where are your knees?",
    options: [
      o("0_0", "Roughly a right angle, thighs about parallel to the floor", 0),
      o("1_1", "Knees higher than hips (seat too low)", 1),
      o("2_2", "Knees lower than hips, or my feet don't reach", 2),
    ],
  },
  {
    id: "q4_2",
    category: 4,
    prompt: "Does your chair support your lower back?",
    options: [
      o("0_0", "Yes, I sit back and my lower back is supported", 0),
      o("1_1", "Partly, but I tend to sit forward", 1),
      o("2_2", "No, no support, or I perch forward", 2),
    ],
  },
  {
    id: "q4_3",
    category: 4,
    prompt: "Can you adjust your chair height and backrest?",
    options: [
      o("0_0", "Yes, both", 0),
      o("1_1", "One but not the other", 1),
      o("2_2", "No, it's a fixed chair, stool, or kitchen chair", 2),
    ],
  },
  {
    id: "q4_4",
    category: 4,
    prompt: "Are your feet flat on the floor or a footrest at your workstation?",
    options: [
      o("0_0", "Yes", 0),
      o("1_1", "Sometimes", 1),
      o("2_2", "No, they dangle or don't reach", 2),
    ],
  },
  {
    id: "q4_5",
    category: 4,
    prompt: "Is your desk or table at a comfortable height for typing?",
    options: [
      o("0_0", "Yes, elbows roughly at desk height when seated", 0),
      o("2_1", "Too high, I raise my shoulders to type", 2),
      o("2_2", "Too low, I hunch forward", 2),
    ],
  },
  {
    id: "q4_6",
    category: 4,
    prompt: "Is there enough space to arrange your screen, keyboard, and documents?",
    options: [
      o("0_0", "Yes, comfortably", 0),
      o("1_1", "A bit cramped", 1),
      o("2_2", "Very cramped", 2),
    ],
  },
  {
    id: "q4_7",
    category: 4,
    prompt:
      "Can you vary between sitting and standing during your day (a sit-stand desk, or a standing option)?",
    options: [o("0_0", "Yes", 0), o("1_1", "No", 1)],
  },
  {
    id: "q5_1",
    category: 5,
    prompt: "Is the lighting in your workspace comfortable for screen work?",
    options: [
      o("0_0", "Yes", 0),
      o("1_1", "Too dim or too bright at times", 1),
      o("2_2", "Consistently uncomfortable", 2),
    ],
  },
  {
    id: "q5_2",
    category: 5,
    prompt: "Is your workspace comfortable to concentrate in, temperature, air, and noise?",
    options: [
      o("0_0", "Yes, generally", 0),
      o("1_1", "One or two things bother me sometimes", 1),
      o("2_2", "Often uncomfortable or distracting", 2),
    ],
  },
  {
    id: "q5_3",
    category: 5,
    prompt: "Do you have enough room to change position and move at your workstation?",
    options: [
      o("0_0", "Yes", 0),
      o("1_1", "A little cramped", 1),
      o("2_2", "No, very restricted", 2),
    ],
  },
  {
    id: "q6_1",
    category: 6,
    prompt:
      "In a typical working hour, do you get short breaks or changes of activity away from the screen?",
    options: [
      o("0_0", "Yes, regularly", 0),
      o("1_1", "Occasionally", 1),
      o("2_2", "Rarely or never", 2),
    ],
  },
  {
    id: "q6_2",
    category: 6,
    prompt:
      "Do you spend long unbroken stretches, two hours or more, on intensive screen or keyboard work?",
    options: [o("0_0", "Rarely", 0), o("1_1", "Sometimes", 1), o("2_2", "Most days", 2)],
  },
];

export const DSE_CATEGORIES: readonly DseCategory[] = [1, 2, 3, 4, 5, 6];

/** Answers keyed by item id; the value is the chosen option's value. */
export type DseAnswers = Readonly<Record<string, string>>;

export interface DseCategoryResult {
  category: DseCategory;
  key: `cat${DseCategory}`;
  label: string;
  points: number;
  max: number;
  /** Out of 100, so it reads the same way as the overall figure. */
  score: number;
  rag: Rag;
}

export interface DseResult {
  instrumentVersion: typeof DSE_INSTRUMENT_VERSION;
  workLocation: WorkLocation;
  categories: DseCategoryResult[];
  totalPoints: number;
  totalMax: number;
  overall: number;
  headline: DseHeadline;
  headlineRag: Rag;
  /** Items scoring the maximum, worst first by category. */
  flagged: string[];
  /** A flagged item the person cannot fix themselves: needs a reviewer. */
  reviewRequired: boolean;
  /** Every non-skipped item had a recognised answer. */
  complete: boolean;
}

export function isSkipped(item: DseItem, answers: DseAnswers): boolean {
  return item.skipWhen !== undefined && answers[item.skipWhen.item] === item.skipWhen.value;
}

export function dseScoreFor(item: DseItem, answers: DseAnswers): DseScore {
  if (isSkipped(item, answers)) return 0;
  return item.options.find((x) => x.value === answers[item.id])?.score ?? 0;
}

/** Items whose flagged state cannot be resolved by the person at that location. */
export function isNonResolvable(itemId: string, location: WorkLocation): boolean {
  if (itemId === "q4_3") return true;
  return (
    (location === "home_improvised" || location === "home_sofa") &&
    (itemId === "q4_1" || itemId === "q4_2" || itemId === "q4_5")
  );
}

const HEADLINE_RAG: Record<DseHeadline, Rag> = {
  low_risk: "green",
  some_adjustments: "amber",
  high_load: "red",
};

export function scoreDse(answers: DseAnswers, workLocation: WorkLocation): DseResult {
  let complete = true;
  const flagged: string[] = [];
  const categories: DseCategoryResult[] = DSE_CATEGORIES.map((cat) => {
    let points = 0;
    let twos = 0;
    for (const item of DSE_ITEMS) {
      if (item.category !== cat) continue;
      if (!isSkipped(item, answers) && !item.options.some((x) => x.value === answers[item.id])) {
        complete = false;
      }
      const s = dseScoreFor(item, answers);
      points += s;
      if (s === 2) {
        twos += 1;
        flagged.push(item.id);
      }
    }
    const max = DSE_CATEGORY_MAX[cat];
    // A category goes red on two maximum answers, or on one once the category
    // is already past 40 percent of its ceiling. A single serious problem is
    // not averaged away by five compliant ones.
    const amberCeiling = Math.round(0.4 * max);
    const rag: Rag =
      points === 0 ? "green" : twos >= 2 || (twos === 1 && points > amberCeiling) ? "red" : "amber";
    return {
      category: cat,
      key: `cat${cat}`,
      label: DSE_CATEGORY_LABEL[cat],
      points,
      max,
      score: Math.round(100 * (1 - points / max)),
      rag,
    };
  });

  const totalPoints = categories.reduce((s, c) => s + c.points, 0);
  const totalMax = categories.reduce((s, c) => s + c.max, 0);
  const overall = Math.round(100 * (1 - totalPoints / totalMax));
  const headline: DseHeadline = categories.some((c) => c.rag === "red")
    ? "high_load"
    : categories.some((c) => c.rag === "amber")
      ? "some_adjustments"
      : "low_risk";

  return {
    instrumentVersion: DSE_INSTRUMENT_VERSION,
    workLocation,
    categories,
    totalPoints,
    totalMax,
    overall,
    headline,
    headlineRag: HEADLINE_RAG[headline],
    flagged,
    reviewRequired: flagged.some((id) => isNonResolvable(id, workLocation)),
    complete,
  };
}
